import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  Popover,
  Modal,
  // FormControl,
  // Select,
} from '@mui/material';
import { Add, Close, KeyboardArrowDown, Search } from '@mui/icons-material';
import { Close as CloseIcon } from '@mui/icons-material';
// import SearchIcon from '@mui/icons-material/Search';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import type { NewIdeationState } from '../../slices/ideationSlice/newIdeationSlice';
import ShowToast, { ToastTypeEnum } from '../../common/components/ShowToast';
import { marked } from 'marked';
import RichTextEditor from '../../common/components/RichTextEditor/RichTextEditor';

import { createIdeationIntake } from '../DataXPLR/IdeationService';
import { deleteQueryHistory } from '../../services/ideationService';

interface IdeationIntake {
  id: string;
  type: string;
  use_case_id: string;
  conversation_id: string;
  created_by_name: string;
  version: string;
  function: string;
  process: string;
  ideation_name: string;
  level2_name: string;
}

export interface ChatSession {
  id: string;
  use_case_title: string;
  session_name: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  usedInProcess: string;
  version?: string;
  use_case_id?: string;
  conversation_id: string;
  ideation_intake?: IdeationIntake[];
  semantic_knowledge?:
    | {
        file_summary?: string;
        report?: string;
      }
    | Record<string, any>;
  field_type?: string;
}

interface RightChatDrawerProps {
  open: boolean;
  sessions?: ChatSession[];
  currentChatId?: string;
  onClose?: () => void;
  onSelectChat?: (chatId: string) => void;
  onNewChat?: () => void;
  onAdd?: (selectedIds: string[]) => Promise<void>;
  currentFieldLabel?: string;
  onCategoryChange?: (category: string) => void;
  onSearchChange?: (searchQuery: string) => void;
  onRefetch?: () => void;
  useCaseId?: string;
}

const RightChatDrawer: React.FC<RightChatDrawerProps> = ({
  open,
  sessions,
  onClose,
  onAdd,
  currentFieldLabel = '',
  onCategoryChange,
  onSearchChange,
  useCaseId,
  onRefetch,
}) => {
  const newIdeationFormState = useSelector<RootState, NewIdeationState>(
    (state) => state.newIdeation
  );
  // const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedSession, setSelectedSession] = useState<ChatSession | null>(null);
  const [selectedSessions, setSelectedSessions] = useState<ChatSession[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [category, setCategory] = useState('Current Process');
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [intakeAnchorEl, setIntakeAnchorEl] = useState<null | HTMLElement>(null);
  const [hoveredIntakes, setHoveredIntakes] = useState<IdeationIntake[]>([]);
  const intakeCloseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const [pendingIntake, setPendingIntake] = useState<{
    conversationIds: string[];
    versions: string[];
    fieldType: string;
    createdBy: string;
  } | null>(null);

  // const [filter, setFilter] = useState<keyof typeof labels>('current');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // const labels = {
  //   current: 'Current Process',
  //   expected: 'Expected Impact',
  //   kpi: 'KPIs',
  //   idea: 'Specific Idea of Improvement',
  // } as const;

  const navigate = useNavigate();

  const [openViewModal, setOpenViewModal] = useState(false);

  const handleClose = () => {
    setSelectedIds([]);
    onClose?.();
  };

  const hasIdeationFormContent = useMemo(() => {
    if (!newIdeationFormState) {
      return false;
    }

    const {
      domain,
      selectedFunction,
      selectedProcessGroup,
      selectedProcess,
      currentProcess,
      expectedImpact,
      specificIdeasOfImprovement,
      keyPerformanceIndicators,
      technologies,
    } = newIdeationFormState;

    const hasTextContent =
      Boolean(currentProcess && currentProcess.trim()) ||
      Boolean(expectedImpact && expectedImpact.trim()) ||
      Boolean(specificIdeasOfImprovement && specificIdeasOfImprovement.trim()) ||
      Boolean(keyPerformanceIndicators && keyPerformanceIndicators.trim());

    const hasHierarchySelection =
      Boolean(domain) ||
      Boolean(selectedFunction) ||
      Boolean(selectedProcessGroup) ||
      Boolean(selectedProcess);

    const hasTechnologies = Array.isArray(technologies) && technologies.length > 0;

    return hasTextContent || hasHierarchySelection || hasTechnologies;
  }, [newIdeationFormState]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, session: ChatSession) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget as HTMLElement);
    setSelectedSession(session);
  };

  // console.log('selectedIds:', conversation_id);

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const categoryMap: Record<string, string> = {
    'Current Process': 'current',
    'Expected Impact': 'expected',
    KPIs: 'performance',
    'Specific Idea of Improvement': 'idea',
  };

  const filteredSessions = sessions?.filter((s) => {
    // Commenting inner filtering for now!
    // const name = s.session_name ?? '';
    const process = s.usedInProcess ?? '';

    // const matchesSearch = name.toLowerCase().includes(searchQuery.toLowerCase());

    const keyword = categoryMap[category] ?? '';
    const matchesCategory = process.toLowerCase().includes(keyword.toLowerCase());

    const hasValidReport = s.semantic_knowledge?.report !== null;

    // return matchesSearch && matchesCategory && hasValidReport;
    return matchesCategory && hasValidReport;
  });

  const handleAddClick = async () => {
    if (selectedIds.length === 0) return;

    const conversationIds: string[] = [];
    const versions: string[] = [];
    let fieldType = '';
    let createdBy = '';

    // Map display category names to API field types
    const fieldTypeMap: Record<string, string> = {
      'Current Process': 'current_process',
      'Expected Impact': 'expected_impact',
      KPIs: 'key_performance_indicator',
      'Specific Idea of Improvement': 'ideas_of_improvement',
    };

    selectedIds.forEach((id) => {
      const session = sessions?.find((s) => s.id === id);
      if (session?.conversation_id) {
        conversationIds.push(session.conversation_id);
        versions.push(session.version || '');

        if (!fieldType && session.usedInProcess) {
          // Map the display name to the API field type, fallback to original if not found
          fieldType = fieldTypeMap[session.usedInProcess] || session.usedInProcess;
        }
        if (!createdBy && session.created_by) {
          createdBy = session.created_by;
        }
      }
    });

    try {
      const organizationId = localStorage.getItem('current_organization') || '';

      if (useCaseId && organizationId) {
        const userName = localStorage.getItem('user_name') || 'admin';

        await createIdeationIntake({
          use_case_id: useCaseId,
          conversation_ids: conversationIds,
          type: fieldType,
          organization_id: organizationId,
          created_by_name: createdBy || userName,
          versions: versions,
        });
        setPendingIntake(null);
      } else if (conversationIds.length > 0) {
        // Defer intake until useCaseId becomes available.
        setPendingIntake({
          conversationIds,
          versions,
          fieldType,
          createdBy,
        });
      }

      await onAdd?.(selectedIds);
      setSelectedIds([]);
      handleClose();
    } catch (error) {
      console.error('Error creating ideation intake:', error);
      await onAdd?.(selectedIds);
      setSelectedIds([]);
      handleClose();
    }
  };

  useEffect(() => {
    if (!pendingIntake) return;

    const organizationId = localStorage.getItem('current_organization') || '';
    if (!useCaseId || !organizationId || pendingIntake.conversationIds.length === 0) {
      return;
    }

    const userName = localStorage.getItem('user_name') || 'admin';

    (async () => {
      try {
        await createIdeationIntake({
          use_case_id: useCaseId,
          conversation_ids: pendingIntake.conversationIds,
          type: pendingIntake.fieldType,
          organization_id: organizationId,
          created_by_name: pendingIntake.createdBy || userName,
          versions: pendingIntake.versions,
        });
      } catch (err) {
        console.error('Error creating deferred ideation intake:', err);
      } finally {
        setPendingIntake(null);
      }
    })();
  }, [useCaseId, pendingIntake]);

  const handleToggleSelection = (sessionId: string) => {
    if (selectedIds.includes(sessionId)) {
      setSelectedIds(selectedIds.filter((id) => id !== sessionId));
    } else {
      setSelectedIds([...selectedIds, sessionId]);
    }
  };

  const handleKnowledgeSession = () => {
    if (selectedSession?.conversation_id) {
      navigate(
        `/data-xplr/${selectedSession.conversation_id}?selectedCategory=${selectedSession?.field_type || getFieldType()}`,
        {
          state: { fromDrawer: true, selectedCategory: currentFieldLabel },
        }
      );
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedSession?.conversation_id) {
      try {
        await deleteQueryHistory(selectedSession?.conversation_id, getFieldType());
        handleCloseMenu();
        if (onRefetch) {
          console.log('Calling onRefetch');
          onRefetch();
        }
        setOpenDeleteConfirm(false);
      } catch (error) {
        console.error('Failed to delete session:', error);
      }
    } else {
      console.log('Closing delete modal - no session');
      setOpenDeleteConfirm(false);
    }
  };

  const handleIntakeHover = (event: React.MouseEvent<HTMLElement>, intakes: IdeationIntake[]) => {
    if (intakes && intakes.length > 0) {
      if (intakeCloseTimeoutRef.current) {
        clearTimeout(intakeCloseTimeoutRef.current);
        intakeCloseTimeoutRef.current = null;
      }
      setIntakeAnchorEl(event.currentTarget);
      setHoveredIntakes(intakes);
    }
  };

  const handleIntakeClose = () => {
    intakeCloseTimeoutRef.current = setTimeout(() => {
      setIntakeAnchorEl(null);
      setHoveredIntakes([]);
    }, 100);
  };

  const handleIntakePopoverEnter = () => {
    if (intakeCloseTimeoutRef.current) {
      clearTimeout(intakeCloseTimeoutRef.current);
      intakeCloseTimeoutRef.current = null;
    }
  };

  const capitalizeName = (name: string) => {
    if (!name) return name;
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getFieldType = () => {
    const fieldTypeMap: Record<string, string> = {
      'Current Process': 'current_process',
      'Expected Impact': 'expected_impact',
      'Specific Idea(s) for Improvement': 'ideas_of_improvement',
      'Key Performance Information': 'key_performance_indicator',
      'Specific Idea of Improvement': 'ideas_of_improvement',
      KPIs: 'key_performance_indicator',
    };
    return fieldTypeMap[currentFieldLabel] || '';
  };
  useEffect(() => {
    setCategory(currentFieldLabel);
  }, [currentFieldLabel]);

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const time = date
      .toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      })
      .replace(' ', '');
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    return `· ${time} ${day} ${month}`;
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 450,
            margin: '16px 16px',
            borderLeft: '1px solid #EBEBEB',
            backgroundColor: '#fff',
            boxShadow: '0 0 20px rgba(0,0,0,0.15)',
            borderRadius: '8px',
            overflow: 'hidden',
            height: 'calc(100vh - 98px)',
            top: '56px',
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0,0,0,0.2)',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            pb: 1,
          }}
        >
          <Typography
            fontSize={18}
            fontWeight={600}
            sx={{
              color: '#141426',
            }}
          >
            Add from Data XPLR
          </Typography>

          <IconButton onClick={handleClose} size="small">
            <img src="/images/cross-drawer.svg" width="14px" height="14px" />
          </IconButton>
        </Box>

        <Typography
          sx={{
            px: 2,
            pb: 2,
            fontSize: '15px',
            fontWeight: 500,
            color: '#666',
            lineHeight: 1.4,
          }}
        >
          Create new knowledge or add existing one from your organisation.
        </Typography>

        {/* Create New Knowledge Button */}
        <Box
          onClick={() => {
            const fieldType = getFieldType();
            if (hasIdeationFormContent) {
              ShowToast({
                message: 'Your ideation draft has been saved',
                type: ToastTypeEnum.Success,
              });
            }
            navigate('/data-xplr', {
              state: {
                fromDrawer: true,
                fieldType,
                selectedCategory: currentFieldLabel,
              },
            });
          }}
          sx={{
            mx: 2,
            mb: 2,
            p: 1.5,
            borderRadius: '6px',
            color: '#1A55F9',
            border: 'none',
            bgcolor: '#E8F0FE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            gap: 1,
            cursor: 'pointer',
            '&:hover': { backgroundColor: '#D6E4FC' },
          }}
        >
          <Add sx={{ fontSize: 18 }} />
          <Typography fontSize={15} fontWeight={500}>
            Create New Knowledge
          </Typography>
        </Box>

        {/* Category Filter */}
        {/* <Box sx={{ px: 2, pb: 1.5 }}>
          <FormControl fullWidth size="small">
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value as keyof typeof labels)}
              renderValue={() => (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography>Category:</Typography>
                  <Typography>{labels[filter]}</Typography>
                </Box>
              )}
            >
              <MenuItem value="current">Current Process</MenuItem>
              <MenuItem value="expected">Expected Impact</MenuItem>
              <MenuItem value="kpi">KPIs</MenuItem>
              <MenuItem value="idea">Specific Idea of Improvement</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ px: 2, pb: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ fontSize: 20, color: '#999' }} />
                </InputAdornment>
              ),
              sx: {
                fontSize: 13,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E0E0E0',
                },
              },
            }}
          />
        </Box> */}

        <Box sx={{ mb: 2, px: 2 }}>
          <TextField
            select
            fullWidth
            value={category}
            onChange={(e) => {
              const newCategory = e.target.value;
              setCategory(newCategory);
              if (onCategoryChange) {
                onCategoryChange(newCategory);
                setSearchQuery('');
              }
            }}
            onPaste={(e) => {
              const pastedText = e.clipboardData.getData('text');

              // optional: delay so input updates first
              setTimeout(() => {
                setSearchQuery(pastedText);
              }, 0);
            }}
            size="small"
            SelectProps={{
              native: false,

              IconComponent: (props) => (
                <KeyboardArrowDown
                  {...props}
                  sx={{ ...props.sx, color: '#131426 !important', fontSize: 24 }}
                />
              ),
              renderValue: (selected: any) => (
                <>
                  <span
                    style={{
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#737373',
                      textAlign: 'left',
                    }}
                  >
                    Category:{' '}
                  </span>
                  <span
                    style={{
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#000000',
                      textAlign: 'right',
                    }}
                  >
                    {selected}
                  </span>
                </>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                fontSize: '14px',
                '& fieldset': {
                  borderColor: '#8A8F9B',
                },
                '&:hover fieldset': {
                  borderColor: '#d1d5db',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                  borderWidth: '1px',
                },
              },
              '& .MuiSelect-icon': {
                color: '#131426',
              },
              '& .MuiOutlinedInput-input': {
                padding: '10px 12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',

                paddingRight: '32px !important',
              },
            }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Current Process">Current Process</MenuItem>
            <MenuItem value="Expected Impact">Expected Impact</MenuItem>
            <MenuItem value="Specific Idea(s) for Improvement">
              Specific Idea(s) for Improvement
            </MenuItem>
            <MenuItem value="KPIs">KPIs</MenuItem>
          </TextField>
        </Box>

        {/* Search Bar */}
        <Box sx={{ px: 2 }}>
          <TextField
            fullWidth
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => {
              const newSearchQuery = e.target.value;
              setSearchQuery(newSearchQuery);
              if (onSearchChange) {
                onSearchChange(newSearchQuery);
              }
            }}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search sx={{ color: '#737373', fontSize: '20px' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,

              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                fontSize: '14px',
                '& fieldset': {
                  borderColor: '#8A8F9B',
                },
                '&:hover fieldset': {
                  borderColor: '#d1d5db',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                  borderWidth: '1px',
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '10px 12px',
                '&::placeholder': {
                  color: '#7B7B7B',
                  opacity: 1,
                },
              },
              '& input::placeholder': {
                color: '#7B7B7B !important',
                opacity: 1,
              },
            }}
          />
        </Box>

        {/* Selection Count */}
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography fontSize={12} fontWeight={500} color="#666">
            {selectedIds.length} selected
          </Typography>
        </Box>

        {/* Chat List */}
        <Box sx={{ overflowY: 'auto', flex: 1, px: 2, pb: 2 }}>
          <List sx={{ p: 0 }}>
            {(filteredSessions || [])?.map((session) => (
              <ListItem
                key={session.id}
                sx={{
                  mb: 1,
                  border: selectedIds.includes(session.id)
                    ? '1px solid #1A55F9'
                    : '1px solid #E0E0E0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'flex-start',
                  bgcolor: '#fff',
                  p: 1.5,
                  '&:hover': {
                    bgcolor: selectedIds.includes(session.id) ? '#F5F9FF' : '#FAFAFA',
                  },
                }}
                onClick={() => handleToggleSelection(session.id)}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: selectedIds.includes(session.id)
                      ? '2px solid #1A55F9'
                      : '2px solid #D9D9D9',
                    bgcolor: selectedIds.includes(session.id) ? '#1A55F9' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                    flexShrink: 0,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleSelection(session.id);
                  }}
                >
                  {selectedIds.includes(session.id) && (
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>
                      {selectedIds.indexOf(session.id) + 1}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <ListItemText
                    // primary={
                    //   session.session_name && session.session_name.trim() !== ''
                    //     ? session.session_name
                    //     : 'Untitled'
                    // }
                    primary={session.use_case_title}
                    primaryTypographyProps={{
                      fontSize: 16,
                      fontWeight: 500,
                      color: '#313131',
                      mb: 0.5,
                    }}
                    secondary={
                      <>
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', fontSize: 12, color: '#666' }}
                        >
                          {session.updated_by
                            ? `updated by ${capitalizeName(session.updated_by)}`
                            : `created by ${capitalizeName(session.created_by)}`}{' '}
                          {formatDateTime(session.created_at)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', fontSize: 12, color: '#666' }}
                        >
                          Used in{' '}
                          {/* <span style={{ textDecoration: 'underline' }}>
                            {session.usedInProcess}
                          </span> */}
                          {session.ideation_intake && session.ideation_intake.length >= 0 && (
                            <>
                              <span
                                style={{
                                  fontSize: 12,
                                  color: '#666',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                }}
                                onClick={(e: React.MouseEvent<HTMLElement>) =>
                                  handleIntakeHover(e, session.ideation_intake || [])
                                }
                                onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                                  handleIntakeHover(e, session.ideation_intake || [])
                                }
                                onMouseLeave={handleIntakeClose}
                              >
                                {session.ideation_intake.length > 1
                                  ? session.ideation_intake.length + ' intakes'
                                  : session.ideation_intake.length + ' intake'}
                              </span>
                            </>
                          )}
                        </Typography>
                      </>
                    }
                    sx={{ m: 0 }}
                  />

                  <IconButton
                    sx={{
                      alignSelf: 'flex-start',
                      p: 0.5,
                      ml: 1,
                    }}
                    onClick={(e) => handleOpenMenu(e, session)}
                  >
                    <MoreHorizIcon sx={{ fontSize: 18 }} />
                  </IconButton>

                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{
                      elevation: 0,
                      sx: { boxShadow: 'none', borderRadius: 0, minWidth: 140, mt: 2 },
                    }}
                  >
                    {' '}
                    <MenuItem
                      sx={{ fontSize: 14, fontWeight: 400 }}
                      onClick={() => {
                        setSelectedSession(session);
                        setOpenViewModal(true);
                        handleCloseMenu();
                      }}
                    >
                      {' '}
                      View Knowledge{' '}
                    </MenuItem>{' '}
                    <MenuItem
                      sx={{ fontSize: 14, fontWeight: 400 }}
                      onClick={handleKnowledgeSession}
                    >
                      Edit Knowledge
                    </MenuItem>{' '}
                    <MenuItem
                      sx={{ fontSize: 14, fontWeight: 400, color: '#FF0505' }}
                      onClick={() => {
                        setOpenDeleteConfirm(true);
                        handleCloseMenu();
                      }}
                    >
                      {' '}
                      Delete{' '}
                    </MenuItem>{' '}
                  </Menu>
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1.5, p: 2, borderTop: '1px solid #E0E0E0' }}>
          <Button
            disabled={selectedIds.length === 0}
            variant="outlined"
            sx={{
              flex: 1,
              fontSize: 14,
              fontWeight: 500,
              border: '1px solid #E0E0E0',
              color: '#000',
              textTransform: 'none',
              bgcolor: '#fff',
              '&:hover': {
                bgcolor: '#FAFAFA',
                border: '1px solid #E0E0E0',
              },
              '&:disabled': {
                border: '1px solid #E0E0E0',
                color: '#999',
              },
            }}
            onClick={() => {
              setSelectedSessions(sessions?.filter((s) => selectedIds.includes(s.id)) || []);
              setOpenViewModal(true);
            }}
          >
            Preview
          </Button>

          <Button
            disabled={selectedIds.length === 0}
            variant="contained"
            sx={{
              flex: 1,
              fontSize: 14,
              fontWeight: 500,
              bgcolor: '#1A55F9',
              color: '#FFFFFF',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#1547E0',
                boxShadow: 'none',
              },
              '&:disabled': {
                bgcolor: '#E0E0E0',
                color: '#999',
              },
            }}
            onClick={async () => {
              await handleAddClick();
            }}
          >
            Add
          </Button>
        </Box>
      </Drawer>
      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            borderRadius: '8px',
            minWidth: 160,
            mt: 0.5,
          },
        }}
      >
        <MenuItem
          sx={{ fontSize: 13, py: 1 }}
          onClick={() => {
            setOpenViewModal(true);
            handleCloseMenu();
          }}
        >
          View Knowledge
        </MenuItem>
        <MenuItem sx={{ fontSize: 13, py: 1 }} onClick={handleKnowledgeSession}>
          Edit Knowledge
        </MenuItem>
        <MenuItem
          sx={{ fontSize: 13, py: 1, color: '#FF0505' }}
          onClick={() => setOpenDeleteConfirm(true)}
        >
          Delete
        </MenuItem>
      </Menu>
      {/* View Modal */}
      <Dialog
        open={openViewModal}
        onClose={() => {
          setOpenViewModal(false);
          setSelectedSession(null);
          setSelectedSessions([]);
        }}
        fullWidth
        maxWidth="lg"
      >
        {/* HEADER */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            pl: '24px',
            pb: '8px',
            // borderBottom: '1px solid #E0E0E0',
          }}
        >
          {selectedSessions.length > 0 ? (
            <Typography sx={{ whiteSpace: 'nowrap', fontSize: 18, fontWeight: 500 }}>
              Preview{' '}
              <span style={{ fontSize: 14, fontWeight: 400, color: '#666' }}>
                | {selectedIds.length} knowledge object{selectedIds.length > 1 ? 's' : ''} selected
              </span>
            </Typography>
          ) : (
            <>
              <Typography sx={{ whiteSpace: 'nowrap' }}>
                <span style={{ fontSize: 18, fontWeight: 600 }}>
                  {selectedSession?.use_case_title}
                </span>{' '}
                <span style={{ fontSize: 12, fontWeight: 400, color: '#666' }}>
                  | created by {selectedSession?.created_by} ·
                  {formatDateTime(selectedSession?.created_at || '')}| Used in{' '}
                  {selectedSession?.usedInProcess}
                </span>
              </Typography>
            </>
          )}

          <IconButton
            onClick={() => {
              setOpenViewModal(false);
              setSelectedSession(null);
              setSelectedSessions([]);
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* BODY */}
        <Box
          sx={{
            p: 3,
            pt: 0,
            '& .rich-text-editor.no-hover:hover': {
              border: '1px solid #d4dadf',
            },
          }}
        >
          {selectedSessions.length > 0 ? (
            (() => {
              const combinedContent = selectedSessions
                .map((session) => {
                  const report = session.semantic_knowledge?.report
                    ? (marked(session.semantic_knowledge.report) as string)
                    : 'No summary available for this session.';
                  return report;
                })
                .join('\n\n---\n\n');
              return (
                <RichTextEditor
                  content={combinedContent}
                  onChange={() => {}}
                  readOnly={true}
                  showToolbarOnFocus={false}
                  enableComments={false}
                  height="auto"
                  maxHeight="60vh"
                  disableHover={true}
                />
              );
            })()
          ) : selectedSession?.semantic_knowledge?.report ? (
            <RichTextEditor
              content={marked(selectedSession.semantic_knowledge.report) as string}
              onChange={() => {}}
              readOnly={true}
              showToolbarOnFocus={false}
              enableComments={false}
              height="auto"
              maxHeight="60vh"
              disableHover={true}
            />
          ) : (
            <Typography sx={{ fontSize: 14, lineHeight: 1.6 }}>No summary available</Typography>
          )}
        </Box>
      </Dialog>
      {/* Intake Details Popover */}
      <Popover
        open={Boolean(intakeAnchorEl) && hoveredIntakes.length > 0}
        anchorEl={intakeAnchorEl}
        onClose={handleIntakeClose}
        disableRestoreFocus
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          pointerEvents: 'none',
        }}
        PaperProps={{
          sx: {
            pointerEvents: 'auto',
            backgroundColor: '#ffffff',
            mt: 0.1,
          },
          onMouseEnter: handleIntakePopoverEnter,
          onMouseLeave: handleIntakeClose,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '250px', p: 1 }}>
          {hoveredIntakes.map((intake, index) => (
            <Box
              key={intake.id || index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 0.75,
                backgroundColor: '#FFFFFF',
                borderRadius: '6px',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: 'Poppins',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#000000',
                  }}
                >
                  Ideation XPLR &gt; {intake.ideation_name || 'N/A'} &gt; {intake.type || 'N/A'}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#6B7280',
                  ml: 2,
                }}
              >
                {intake.version || 'N/A'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
      <Modal
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: 24,
            p: 0,
            minWidth: 700,
            maxWidth: 700,
            outline: 'none',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              backgroundColor: '#1A55F9',
              borderRadius: '8px 8px 0 0',
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#fff' }}>
              Delete Knowledge
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpenDeleteConfirm(false)}
              sx={{
                color: '#fff',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2 }}>
            <Typography sx={{ fontSize: '16px', color: '#333' }}>
              Do you want to delete this Knowledge?
            </Typography>
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              p: 2,
              borderTop: '1px solid #E5E5E5',
            }}
          >
            <Button
              onClick={() => setOpenDeleteConfirm(false)}
              sx={{
                color: '#666',
                textTransform: 'none',
                fontSize: '14px',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              No
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: '#1A55F9',
                textTransform: 'none',
                fontSize: '14px',
                '&:hover': { backgroundColor: '#1547D9' },
              }}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default RightChatDrawer;
